import { useRef, type ReactNode } from 'react'
import { cx } from '../cx'
import { SectionLabel, type LabelTone } from './SectionLabel'
import styles from './Card.module.css'

/**
 * Which section of the sheet a card belongs to. Spec §4 gives every card a 4px coloured left
 * spine; the variant picks which colour (see Card.module.css for the assignment and the
 * contrast measurements behind it).
 *
 * On a phone the spine is the fastest way to tell where you are in a long scroll, so no two
 * cards that sit next to each other share one. `magic` is deliberately shared by all four
 * spellcasting cards: they are one subject, and four colours for specializations, focus items,
 * enchanted items and rote spells would be four ideas where the sheet has one.
 */
export type CardVariant =
  | 'plain'
  | 'aspects'
  | 'skills'
  | 'harm'
  | 'fate'
  | 'powers'
  | 'stunts'
  | 'magic'
  | 'equipment'
  | 'bio'

/**
 * The label tone per variant.
 *
 * Deliberately *not* one tone per spine colour. A 4px bar and 12px uppercase text have
 * different jobs: half the palette (fae at 1.6:1, rust at 1.4:1 on the card surface) is far
 * too dark to read as a label, and lightening it for text would put a second, near-miss
 * version of each colour into the system. The spine carries the colour code; the label stays
 * in the three tones §3.1 gives text — with `arcane` tying the spellcasting cards together.
 */
const LABEL_TONE: Record<CardVariant, LabelTone> = {
  plain: 'ember',
  aspects: 'ember',
  skills: 'ember',
  harm: 'blood',
  fate: 'ember',
  powers: 'ember',
  stunts: 'ember',
  magic: 'arcane',
  equipment: 'ember',
  bio: 'ember',
}

export interface CardProps {
  children: ReactNode
  variant?: CardVariant
  /** All-caps label, top-left. Coloured to match the spine. */
  label?: string | undefined
  /** Muted hint, top-right — e.g. "TAP TO INVOKE". */
  hint?: string | undefined
  /**
   * Header-right control — the section's add or edit action, as an {@link IconButton}.
   * It lives here rather than as a full-width button under the content because a card can be
   * long: on a phone the edit control for aspects used to sit below nine chips.
   */
  action?: ReactNode
  className?: string | undefined
  id?: string | undefined
  /**
   * The card has nothing in it yet — it is on screen only because its owner may add to it.
   * Published as `data-empty` for anything that indexes the page: the sheet's section nav
   * skips these, since offering a jump to "None yet." is offering nothing.
   */
  empty?: boolean | undefined
}

export function Card({
  children,
  variant = 'plain',
  label,
  hint,
  action,
  className,
  id,
  empty,
}: CardProps) {
  const hasHead = label !== undefined || hint !== undefined || action !== undefined

  return (
    <section
      id={id}
      className={cx(styles.card, styles[variant], className)}
      {...(empty === true && { 'data-empty': '' })}
    >
      {hasHead && (
        <header className={styles.head}>
          {label !== undefined && <SectionLabel tone={LABEL_TONE[variant]}>{label}</SectionLabel>}
          <span className={styles.headEnd}>
            {hint !== undefined && <span className={styles.hint}>{hint}</span>}
            {action}
          </span>
        </header>
      )}
      {children}
    </section>
  )
}

export interface CollapsibleCardProps extends CardProps {
  /** Spec §5.1: powers, stunts and magic collapse so the top of the sheet stays fast to scan. */
  defaultOpen?: boolean
  /** Optional count or summary shown next to the label while collapsed. */
  summary?: string | undefined
}

/**
 * A card that collapses. Built on `<details>`/`<summary>` so keyboard operation,
 * the expanded/collapsed announcement, and find-in-page all come from the platform
 * rather than from an aria-expanded button we would have to maintain.
 *
 * The action sits *outside* the `<details>`, positioned over the header row, because a
 * `<summary>` is itself an interactive control: a button nested in one is a nested-interactive
 * failure — screen readers may never announce it, and focus order stops being predictable.
 * Being outside also means it needs no event gymnastics to avoid toggling the disclosure.
 */
export function CollapsibleCard({
  children,
  variant = 'plain',
  label,
  hint,
  action,
  summary,
  defaultOpen = false,
  className,
  id,
  empty,
}: CollapsibleCardProps) {
  const details = useRef<HTMLDetailsElement>(null)

  return (
    <section
      id={id}
      className={cx(styles.card, styles[variant], className)}
      {...(empty === true && { 'data-empty': '' })}
    >
      <details ref={details} open={defaultOpen} className={styles.details}>
        <summary
          className={cx(styles.head, styles.summary, action !== undefined && styles.hasAction)}
        >
          <span className={styles.summaryText}>
            {label !== undefined && <SectionLabel tone={LABEL_TONE[variant]}>{label}</SectionLabel>}
            {summary !== undefined && <span className={styles.hint}>{summary}</span>}
          </span>
          <span className={styles.headEnd}>
            {hint !== undefined && <span className={styles.hint}>{hint}</span>}
            <svg
              className={styles.chevron}
              viewBox="0 0 16 16"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M4 6l4 4 4-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </summary>
        <div className={styles.collapsibleBody}>{children}</div>
      </details>

      {/* Opens the card rather than toggling it: the form the action mounts lives in the
          body, so pressing "add" on a closed card has to reveal it. */}
      {action !== undefined && (
        <span
          className={styles.floatingAction}
          onClick={() => {
            if (details.current !== null) details.current.open = true
          }}
        >
          {action}
        </span>
      )}
    </section>
  )
}
